/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { MenuItem } from '@mui/material';
import type { SxProps } from '@mui/system';
import React from 'react';
import type { MosaicBranch, MosaicNode } from 'react-mosaic-component';
import {
  getAndAssertNodeAtPathExists,
  MosaicContext,
} from 'react-mosaic-component';
import { useDispatch, useSelector } from 'react-redux';
import { CustomIconButton } from '@opengeoweb/shared';
import {
  SplitVertical,
  SplitHorizontal,
  SplitVerticalWithContent,
  SplitHorizontalWithContent,
} from '@opengeoweb/theme';
import { getUniqueId } from './helpers';
import type { AppStore } from '../../store/types';
import { workspaceActions } from '../../store/workspace';
import {
  getViewIds,
  getViewComponentType,
} from '../../store/workspace/selectors';
import type { WorkspaceSetNewViewPayload } from '../../store/workspace/types';
import { useWorkspaceTranslation } from '../../utils/i18n';

const wrapInMenuItem = (
  inMenuItem: boolean,
  component: React.ReactNode,
  isDisabled = false,
): React.ReactNode => {
  if (inMenuItem) {
    return <MenuItem disabled={isDisabled}>{component}</MenuItem>;
  }
  return component;
};

const buttonStyle: SxProps = { marginLeft: 0.5, marginRight: 0.5 };

interface SplitButtonsConnectProps {
  panelId: string;
  path: MosaicBranch[];
  inMenuItem?: boolean;
}

export const SplitButtonsConnect: React.FC<SplitButtonsConnectProps> = ({
  panelId,
  path,
  inMenuItem = false,
}: SplitButtonsConnectProps) => {
  const { t } = useWorkspaceTranslation();
  const { mosaicActions: actions } = React.useContext(MosaicContext);
  const dispatch = useDispatch();
  const viewIds: string[] = useSelector((store: AppStore) => getViewIds(store));

  const viewComponentType = useSelector((store: AppStore) =>
    getViewComponentType(store, panelId),
  );

  const addView = React.useCallback(
    (view: WorkspaceSetNewViewPayload): void => {
      dispatch(workspaceActions.setNewWorkspaceView(view));
    },
    [dispatch],
  );

  const createNode = React.useCallback(
    (copyContent: boolean): MosaicNode<string> => {
      const newId = getUniqueId(viewIds);
      addView({
        // If content not copied - open new map
        componentType: copyContent ? viewComponentType : 'Map',
        mosaicNodeId: panelId,
        newId,
        copyOriginalcontent: copyContent,
      });

      return newId;
    },
    [addView, panelId, viewComponentType, viewIds],
  );

  return (
    <>
      {wrapInMenuItem(
        inMenuItem,
        <CustomIconButton
          data-testid="split-horizontal-btn"
          onClick={(event): void => {
            const newNode = createNode(event.shiftKey);
            const previousNode = getAndAssertNodeAtPathExists(
              actions.getRoot(),
              path,
            );
            actions.replaceWith(path, {
              direction: 'column',
              first: previousNode,
              second: newNode,
            });
          }}
          sx={buttonStyle}
          tooltipTitle={`${t('workspace-control-split-horizontal')} - ${t('workspace-control-split-shift')}`}
        >
          <SplitHorizontal />
        </CustomIconButton>,
      )}
      {wrapInMenuItem(
        inMenuItem,
        <CustomIconButton
          data-testid="split-vertical-btn"
          onClick={(event): void => {
            const newNode = createNode(event.shiftKey);
            const previousNode = getAndAssertNodeAtPathExists(
              actions.getRoot(),
              path,
            );
            actions.replaceWith(path, {
              direction: 'row',
              first: previousNode,
              second: newNode,
            });
          }}
          sx={buttonStyle}
          tooltipTitle={`${t('workspace-control-split-vertical')} - ${t('workspace-control-split-shift')}`}
        >
          <SplitVertical />
        </CustomIconButton>,
      )}

      {wrapInMenuItem(
        inMenuItem,
        <CustomIconButton
          data-testid="split-horizontal-btn-copy-content"
          onClick={(event): void => {
            // Always copy over content
            const newNode = createNode(true);
            const previousNode = getAndAssertNodeAtPathExists(
              actions.getRoot(),
              path,
            );
            actions.replaceWith(path, {
              direction: 'column',
              first: previousNode,
              second: newNode,
            });
          }}
          sx={buttonStyle}
          tooltipTitle={t('workspace-control-split-horizontal-with-data')}
        >
          <SplitHorizontalWithContent />
        </CustomIconButton>,
      )}
      {wrapInMenuItem(
        inMenuItem,
        <CustomIconButton
          data-testid="split-vertical-btn-copy-content"
          onClick={(event): void => {
            // Always copy over content
            const newNode = createNode(true);
            const previousNode = getAndAssertNodeAtPathExists(
              actions.getRoot(),
              path,
            );
            actions.replaceWith(path, {
              direction: 'row',
              first: previousNode,
              second: newNode,
            });
          }}
          sx={buttonStyle}
          tooltipTitle={t('workspace-control-split-vertical-with-data')}
        >
          <SplitVerticalWithContent />
        </CustomIconButton>,
      )}
    </>
  );
};
